import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, FileText, Download, Pin } from "lucide-react";
import { PublicLayout } from "@/components/site/PublicLayout";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { api, fileUrl } from "@/lib/api";
import { format } from "date-fns";

const CATEGORIES = [
    "All", "Notices", "Tender Notices", "Circulars", "AGM", "Financial Reports",
    "Audit Reports", "Election Notices", "Meeting Minutes", "Government Orders",
    "Maintenance Notices", "Water Supply Notices", "Others",
];

export default function Documents() {
    const [params, setParams] = useSearchParams();
    const initialCat = params.get("category") || "All";
    const [cat, setCat] = useState(initialCat);
    const [q, setQ] = useState("");
    const [docs, setDocs] = useState([]);

    useEffect(() => {
        const p = { limit: 200, society_id: "none" };
        if (cat !== "All") p.category = cat;
        if (q) p.q = q;
        api.get("/documents", { params: p }).then((r) => setDocs(r.data));
        // sync url
        const next = new URLSearchParams(params);
        cat !== "All" ? next.set("category", cat) : next.delete("category");
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cat, q]);

    const pinned = useMemo(() => docs.filter((d) => d.pinned), [docs]);
    const others = useMemo(() => docs.filter((d) => !d.pinned), [docs]);

    return (
        <PublicLayout>
            <section className="container-x pt-10">
                <Breadcrumbs items={[{ label: "Documents" }]} />
                <div className="mt-5 flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <div className="overline">Records</div>
                        <h1 className="mt-3 font-serif text-5xl text-slate-900 sm:text-6xl">Documents</h1>
                        <p className="mt-4 max-w-2xl text-lg text-slate-600">
                            Every notice, tender, circular and record published by the Association. Filter by category or search below.
                        </p>
                    </div>
                    <div className="relative w-full max-w-xs">
                        <Search size={16} className="absolute left-3 top-3 text-slate-400" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search documents…"
                            className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20"
                            data-testid="documents-search"
                        />
                    </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-2" data-testid="doc-categories">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c}
                            onClick={() => setCat(c)}
                            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${cat === c ? "border-brand-blue bg-brand-blue text-white" : "border-slate-200 bg-white text-slate-700 hover:border-brand-blue hover:text-brand-blue"}`}
                            data-testid={`doc-cat-${c}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </section>

            <section className="container-x mt-10 pb-20">
                {pinned.length > 0 && (
                    <>
                        <div className="overline mb-3 flex items-center gap-2 text-brand-orange"><Pin size={12} /> Pinned</div>
                        <div className="grid gap-3">
                            {pinned.map((d) => <Row key={d.id} d={d} />)}
                        </div>
                        <div className="my-8 h-px bg-slate-200" />
                    </>
                )}
                <div className="grid gap-3">
                    {others.map((d) => <Row key={d.id} d={d} />)}
                    {docs.length === 0 && <div className="py-16 text-center text-slate-500">No documents match your filter.</div>}
                </div>
            </section>
        </PublicLayout>
    );
}

const Row = ({ d }) => (
    <article className="surface-card flex flex-wrap items-center gap-4 p-5" data-testid={`doc-row-${d.id}`}>
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-brand-blue-light text-brand-blue">
            <FileText size={18} />
        </div>
        <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
                {d.pinned && <Pin size={12} className="text-brand-orange" />}
                <span className="font-medium text-slate-900">{d.title}</span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-slate-600">{d.category}</span>
            </div>
            {d.description && <p className="mt-1 line-clamp-1 text-sm text-slate-600">{d.description}</p>}
            <div className="mt-1 text-[11px] text-slate-400">
                {d.created_at ? format(new Date(d.created_at), "dd MMM yyyy") : ""} · {d.download_count} downloads
            </div>
        </div>
        {d.file_id ? (
            <a
                href={fileUrl(d.file_id)}
                target="_blank"
                rel="noreferrer"
                onClick={() => api.post(`/documents/${d.id}/track-download`).catch(() => {})}
                className="btn-outline !py-2"
                data-testid={`download-${d.id}`}
            >
                <Download size={14} /> Download
            </a>
        ) : (
            <span className="rounded-full bg-slate-100 px-4 py-2 text-xs text-slate-500">No file</span>
        )}
    </article>
);
